import { uniqBy } from 'lodash'
import { ReactNode, useMemo, useState } from 'react'
import { twMerge as twm } from 'tailwind-merge'

import Button from '../app/design/Button'
import Label from '../app/design/Label'
import Paragraph from '../app/design/Paragraph'
import { Checkbox } from './fields/Checkbox'
import { SearchField } from './fields/SearchField'

export type SelectListItem = {
    id: string
    title: ReactNode
    subtitle?: string
    thirdLine?: ReactNode
    tertiaryView?: JSX.Element
    secondaryTitle?: string
    button?: {
        title: string
        onClick: () => void
    }
    searchText?: string
    isDisabled?: boolean
    appearance?: 'default' | 'disabled'
}

export type Props<ItemType extends SelectListItem> = {
    selectionType: 'single' | 'multi'
    items: ItemType[]
    selectedItems: ItemType[]
    onSelectItems?: (items: ItemType[]) => void
    canSearch?: boolean
    /** Maximum number of items selectable for multi-select lists */
    selectionLimit?: number
    // Note: in the current implementation this reverses the order of _all_ item elements.
    checkboxSide?: 'left' | 'right'
}

export const SelectList = <ItemType extends SelectListItem>({
    selectionType,
    items,
    selectedItems,
    onSelectItems,
    canSearch,
    checkboxSide = 'right',
    selectionLimit = Infinity,
}: Props<ItemType>) => {
    const [searchText, setSearchText] = useState('')

    const itemsWithState = useMemo(
        () =>
            items.map((item) => {
                const isAtMax =
                    !Number.isNaN(selectionLimit) && selectedItems.length >= selectionLimit
                const isSelected = selectedItems.find((selectedItem) => selectedItem.id === item.id)
                const isDisabled = (isAtMax && !isSelected) || item.isDisabled

                return { ...item, isDisabled }
            }),
        [items, selectedItems, selectionLimit]
    )

    const filteredItems = useMemo(() => {
        if (!canSearch) return itemsWithState

        const queryTokens = searchText.toLowerCase().split(/\s+/) ?? []

        return itemsWithState.filter((item) => {
            const searchableField = (
                item.searchText ?? (typeof item.title === 'string' ? item.title : '')
            ).toLowerCase()
            return queryTokens.every((token) => searchableField.includes(token))
        })
    }, [itemsWithState, searchText, canSearch])

    const selectedItemIDs = selectedItems.map((item) => item.id)

    const handleSelectItem = (item: ItemType) => {
        const isSelected = selectedItems.some((selectedItem) => selectedItem.id === item.id)

        if (selectionType === 'single') {
            if (isSelected) {
                onSelectItems?.([])
            }
            else {
                onSelectItems?.([item])
            }
        }
        else {
            if (isSelected) {
                onSelectItems?.(selectedItems.filter((selectedItem) => selectedItem.id !== item.id))
            }
            else {
                onSelectItems?.(uniqBy([...selectedItems, item], (item) => item.id))
            }
        }
    }

    return (
        <div>
            {canSearch && (
                <SearchField
                    isFullWidth
                    size="medium"
                    value={searchText}
                    onSearch={setSearchText}
                    onClear={() => setSearchText('')}
                />
            )}

            <div
                className={twm(
                    'flex shrink grow flex-col items-stretch justify-start gap-y-3 px-px',
                    canSearch && 'mt-6'
                )}
            >
                {filteredItems.map((item) => {
                    const isSelected = selectedItemIDs.includes(item.id)

                    return (
                        <div
                            key={item.id}
                            className={twm(
                                'flex items-center justify-center gap-x-4 rounded-lg border border-gray-300 p-4',
                                !item.isDisabled
                                    ? 'cursor-pointer hover:bg-black/4 active:bg-black/8'
                                    : 'opacity-50',
                                item.appearance === 'disabled' && 'opacity-50',
                                isSelected &&
                                    'border-secondary-400 outline-secondary-400 outline-1',
                                checkboxSide === 'left' && 'flex-row-reverse'
                            )}
                            onClick={item.isDisabled ? undefined : () => handleSelectItem(item)}
                        >
                            {item.tertiaryView && (
                                <div className="shrink-0">{item.tertiaryView}</div>
                            )}

                            <div className="flex min-w-0 shrink grow flex-col items-stretch justify-start gap-y-1 overflow-x-hidden">
                                {item.subtitle ? (
                                    <Label className="block truncate">{item.title}</Label>
                                ) : (
                                    <Paragraph className="block truncate">{item.title}</Paragraph>
                                )}
                                {item.subtitle && (
                                    <Paragraph
                                        size="sm"
                                        className="block truncate font-normal text-gray-600"
                                    >
                                        {item.subtitle}
                                    </Paragraph>
                                )}
                                {item.thirdLine}
                            </div>

                            {item.secondaryTitle !== undefined && (
                                <Label size="md">{item.secondaryTitle}</Label>
                            )}

                            {item.button !== undefined && (
                                <Button
                                    intent="tertiary_deprecated"
                                    size="small"
                                    onClick={(event: React.MouseEvent) => {
                                        event.stopPropagation()
                                        event.preventDefault()
                                        item.button?.onClick()
                                    }}
                                    className="h-8"
                                >
                                    {item.button.title}
                                </Button>
                            )}

                            <Checkbox
                                disabled={item.isDisabled}
                                isChecked={isSelected}
                                style={selectionType === 'single' ? 'radio' : 'checkbox'}
                                className="shrink-0"
                                onChange={() => handleSelectItem(item)}
                            />
                        </div>
                    )
                })}
            </div>
        </div>
    )
}
